const STATE_PREFIX = 'bj-jewellery-sets-state:v1:';

// Above this HTML size we don't persist the grid markup (sessionStorage stores
// UTF-16, ~2 bytes/char, and Safari's quota is ~5MB). Large grids fall back to
// re-fetching pages on restore instead — see _rebuildByRefetch.
const SAFE_HTML_CHARS = 1_500_000;
const SNAPSHOT_MAX_AGE_MS = 5 * 60 * 1000;

// Upper bound on pages re-fetched during restore, so an extremely deep session
// can't fire hundreds of requests. Beyond this the grid restores partially.
const MAX_RESTORE_PAGES = 25;

// Max concurrent page fetches during restore. Caps the request burst and spreads
// the DOMParser/clone work across the fetch window, protecting INP on restore.
const REFETCH_CONCURRENCY = 6;

// Scroll-spy: a page marker counts as the "current" page once it has scrolled up
// into the top fraction of the viewport — generous enough to clear a sticky header
// and to match where a deep-link scroll lands the marker.
const SPY_TRIGGER_RATIO = 0.35;

// Key by collection + filters/sort, but NOT by page — so all depths of the same
// result set share one slot. Params are sorted so logically-equal URLs collapse
// to a single slot regardless of ordering.
function stateKey() {
  const url = new URL(location.href);
  url.searchParams.delete('page');
  url.searchParams.sort();
  return STATE_PREFIX + url.pathname + url.search;
}

// Parse the 1-based page number from a URL's `?page=` param (defaults to 1).
function pageNum(href) {
  try {
    const p = Number.parseInt(new URL(href, location.origin).searchParams.get('page') || '1', 10);
    return Number.isFinite(p) && p > 0 ? p : 1;
  } catch {
    return 1;
  }
}

// The grid snapshot lives at module scope, decoupled from any single element's
// lifetime. The <bj-infinite-scroll> element removes itself once the final page
// is loaded (see _load), so a listener bound to the element would be gone before
// the user navigates away — exactly the users who most want restoration. A single
// persistent pagehide handler reads this snapshot instead. scrollY is captured at
// pagehide (the moment of leaving), not when the snapshot is taken.
let pendingSnapshot = null;
let pageHideBound = false;

// Restoration must run at most once per document load. The <bj-infinite-scroll>
// element can re-mount within the SAME document — e.g. the theme's facet:update
// replaces the whole collection section when a filter changes — and navigation
// type stays 'back_forward' for the life of the document. Without this guard a
// filter change after a back-nav would fire a second (async, fetching) restore
// that races the first on replaceChildren and rebuilds the pre-filter grid.
let restoredThisLoad = false;

function persistSnapshot() {
  if (!pendingSnapshot) return;
  const { key, html, nextUrl, title, deepestUrl, savedAt } = pendingSnapshot;
  // Lightweight state always persists; it's enough to rebuild via re-fetch.
  const light = { nextUrl, title, deepestUrl, savedAt, scrollY: window.scrollY };
  // Fast path: also stash the grid markup when it's small enough to fit safely.
  // window.__bjRestoreHtmlCap is a test seam to force the re-fetch path.
  const cap = typeof window.__bjRestoreHtmlCap === 'number' ? window.__bjRestoreHtmlCap : SAFE_HTML_CHARS;
  const full = html && html.length <= cap ? { ...light, html } : light;
  try {
    sessionStorage.setItem(key, JSON.stringify(full));
  } catch {
    // Quota exceeded — evict OTHER collection-state slots (stale filter/sort
    // combinations), then retry with lightweight state only (no html blob).
    try {
      for (let i = sessionStorage.length - 1; i >= 0; i--) {
        const k = sessionStorage.key(i);
        if (k && k.startsWith(STATE_PREFIX) && k !== key) sessionStorage.removeItem(k);
      }
      sessionStorage.setItem(key, JSON.stringify(light));
    } catch (retryErr) {
      console.warn('[bj-infinite-scroll] could not persist scroll state', retryErr);
    }
  }
}

function ensurePageHideBound() {
  if (pageHideBound) return;
  pageHideBound = true;
  // pagehide is bfcache-safe (unlike beforeunload/unload); bound once at document level
  window.addEventListener('pagehide', persistSnapshot);
  window.addEventListener('pageshow', (event) => {
    if (event.persisted && pendingSnapshot && Date.now() - pendingSnapshot.savedAt > SNAPSHOT_MAX_AGE_MS) {
      location.reload();
    }
  });
}

class BjJewellerySetsScroll extends HTMLElement {
  connectedCallback() {
    this._productList = document.querySelector('product-list');
    this._button = this.querySelector('[data-load-more-btn]');
    this._sentinel = this.querySelector('[data-sentinel]');

    this._status = document.createElement('p');
    this._status.setAttribute('role', 'status');
    this._status.setAttribute('aria-live', 'polite');
    this._status.className = 'sr-only';
    this._productList?.insertAdjacentElement('afterend', this._status);

    this._button?.addEventListener('click', () => this._load());

    if (this.dataset.mode === 'infinite_scroll' && this._sentinel) {
      this._observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) this._load();
        },
        { rootMargin: '400px' },
      );
      this._observer.observe(this._sentinel);
    }

    // --- scroll-spy: keep the address bar in sync with the scrolled page ---
    // As the streamed grid is scrolled, reflect the page section at the top of the
    // viewport in the URL (?page=N, cleared at page 1). Fires when a page marker
    // crosses the top zone; the handler reads all markers to pick the current page.
    // `_deepestUrl` separately tracks the furthest page actually loaded — the URL
    // now follows scroll rather than load depth, so the re-fetch restore relies on
    // this (not location.href) to rebuild the FULL grid.
    this._deepestUrl = location.href;
    // Track the scrolled page with a rAF-throttled passive scroll listener. This
    // is reliable at any scroll position — including hitting the bottom on a short
    // final page, which a marker-crossing IntersectionObserver would miss — and
    // coalesces to at most one recompute per frame.
    this._spyRaf = 0;
    this._onScroll = () => {
      if (this._spyRaf) return;
      this._spyRaf = requestAnimationFrame(() => {
        this._spyRaf = 0;
        this._syncUrlToScrolledPage();
      });
    };
    window.addEventListener('scroll', this._onScroll, { passive: true });

    // --- scroll restoration on back/forward navigation and reload ---
    ensurePageHideBound();

    // Back/forward may reuse a recent grid. Reloads and direct deep links
    // rebuild from current server content instead of restoring saved HTML.
    const navType = performance.getEntriesByType('navigation')[0]?.type;
    const onDeepPage = new URL(location.href).searchParams.has('page');
    if ((navType === 'back_forward' || navType === 'reload' || onDeepPage) && !restoredThisLoad) {
      // Claim the single restore slot synchronously, before any await, so a
      // concurrent re-mount can't also enter _restore (R2).
      restoredThisLoad = true;
      this._restore().finally(() => this._snapshot());
    } else {
      // Fresh load, or a re-mount after restore already ran (e.g. a filter
      // change): just (re-)seed the snapshot from the current — correctly
      // filtered, server-rendered — grid. dataset.nextUrl is authoritative here.
      this._snapshot();
    }
  }

  // Capture the current grid into the module-level pendingSnapshot (scrollY is
  // added later at pagehide). Pass an explicit nextUrl to override this.dataset
  // (used on the terminal page where dataset.nextUrl is no longer meaningful).
  _snapshot(nextUrlOverride) {
    if (!this._productList) return;
    pendingSnapshot = {
      key: stateKey(),
      savedAt: Date.now(),
      html: this._productList.innerHTML, // dropped at persist time if too large
      nextUrl: nextUrlOverride !== undefined ? nextUrlOverride : this.dataset.nextUrl || '',
      title: document.title,
      // Deepest page reached — used to rebuild via re-fetch. Tracked separately
      // from location.href because the address bar now follows the scrolled page
      // (scroll-spy), which may be shallower than the furthest page loaded.
      deepestUrl: this._deepestUrl || location.href,
    };
  }

  async _restore() {
    let raw = null;
    try {
      if (performance.getEntriesByType('navigation')[0]?.type === 'back_forward') {
        raw = sessionStorage.getItem(stateKey());
      }
    } catch {
      raw = null;
    }

    let state = null;
    if (raw) {
      try {
        state = JSON.parse(raw);
        const age = Date.now() - state.savedAt;
        if (!Number.isFinite(age) || age < 0 || age > SNAPSHOT_MAX_AGE_MS) {
          state = null;
          sessionStorage.removeItem(stateKey());
        }
      } catch {
        state = null;
      }
    }

    // No usable saved state. If this is a cold deep-link to ?page=N (a shared or
    // indexed URL opened without a snapshot from an earlier scroll), the server
    // rendered that page alone — rebuild the full page-1..N grid by re-fetching,
    // then land the visitor on the collection section.
    if (!state || !this._productList) {
      await this._coldDeepLinkRestore();
      return;
    }

    // The snapshot key is page-agnostic (all depths of a result set share one
    // slot), so a snapshot seeded by earlier, shallower browsing can be found when
    // the visitor now deep-links to a DEEPER ?page=N (a shared/indexed URL). That
    // snapshot can't satisfy page N — and the server has already rendered page N
    // with the correct data-next-url — so rebuild pages 1..N from the URL (cold
    // deep-link path) instead of restoring the shallower snapshot, which would drop
    // the requested page and everything between it and the snapshot's depth.
    if (pageNum(location.href) > pageNum(state.deepestUrl)) {
      await this._coldDeepLinkRestore();
      return;
    }

    // Block _load (observer/button) from appending while we rebuild the grid, so
    // an in-flight page load can't race the replaceChildren below. Set before the
    // first await; the observer callback only fires on a later tick.
    this._restoring = true;
    // Silence the live region while we rewrite the whole grid so a screen reader
    // doesn't announce every restored card (WCAG 4.1.3); reset in the finally.
    this._setGridAnnouncements(false);
    try {
      // Only take manual control once we know we have state to honour — otherwise
      // the browser's native (auto) restoration is left to do its best.
      if ('scrollRestoration' in history) history.scrollRestoration = 'manual';

      if (typeof state.html === 'string') {
        // Fast path — the grid markup fit in storage. Trust boundary: state.html is
        // same-origin markup this component wrote from server-rendered product cards,
        // never untrusted input. Don't repurpose this cache for third-party content.
        this._productList.innerHTML = state.html;
      } else if (state.deepestUrl) {
        // Large grid — rebuild by re-fetching the pages the user had loaded.
        await this._rebuildByRefetch(state.deepestUrl);
      }
      if (state.title) document.title = state.title;

      if (state.nextUrl) {
        this.dataset.nextUrl = state.nextUrl;
        const seoAnchor = document.querySelector('a[rel="next"]');
        if (seoAnchor) seoAnchor.href = state.nextUrl;
      } else {
        // All pages had already been loaded — nothing more to fetch.
        this._observer?.disconnect();
        this._sentinel?.remove();
      }

      // On a restored deep page the viewport jumps to the saved scroll position,
      // so the (server-lazy) images above and around it wouldn't otherwise load.
      // Eager-load every image in the rebuilt grid so a reload shows a fully
      // painted page immediately instead of blank/placeholder cards.
      this._eagerizeImages();

      // The furthest loaded page comes from the snapshot, not the address bar
      // (which now follows scroll) — the scroll-spy reads page markers live.
      this._deepestUrl = state.deepestUrl || location.href;
    } finally {
      this._setGridAnnouncements(true);
      this._restoring = false;
    }

    // Let third-party card decorators (Wishlist King, BSS, Novuna) re-init the
    // rebuilt cards, and give tests a deterministic restore signal.
    document.dispatchEvent(new CustomEvent('bj:infinite-scroll:restored'));

    this._restoreScroll(state.scrollY || 0);
  }

  // Rebuild the accumulated grid by re-fetching pages 1..N (N derived from the
  // deepest URL reached), preserving any active filters/sort. Quota-proof at any
  // depth. Pages are fetched in parallel and appended in order.
  async _rebuildByRefetch(deepestUrl) {
    if (!this._productList) return;

    let url;
    try {
      url = new URL(deepestUrl, location.origin);
    } catch {
      return;
    }

    let maxPage = parseInt(url.searchParams.get('page') || '1', 10);
    if (!Number.isFinite(maxPage) || maxPage < 1) maxPage = 1;
    if (maxPage <= 1) return; // page 1 is already rendered by the server

    const capped = maxPage > MAX_RESTORE_PAGES;
    if (capped) maxPage = MAX_RESTORE_PAGES;

    const base = new URL(url);
    base.searchParams.delete('page');

    const pageUrls = [];
    for (let p = 1; p <= maxPage; p++) {
      const u = new URL(base);
      if (p > 1) u.searchParams.set('page', String(p));
      pageUrls.push(u.pathname + u.search);
    }

    // Fetch with bounded concurrency. Each page is parsed into its own fragment
    // as it resolves — spreading the parse/clone cost across the fetch window —
    // and stored by index so the final grid keeps page order regardless of the
    // order responses arrive.
    const slots = new Array(pageUrls.length).fill(null);
    let cursor = 0;
    const worker = async () => {
      while (cursor < pageUrls.length) {
        const i = cursor++;
        try {
          const res = await fetch(pageUrls[i], { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
          if (!res.ok) continue;
          const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
          const items = doc.querySelector('product-list')?.children ?? [];
          const frag = document.createDocumentFragment();
          [...items].forEach((el, idx) => {
            const clone = el.cloneNode(true);
            // Tag the first item of each page (page number = slot index + 1) so
            // any path can scroll to a page's start via [data-bj-page="N"].
            if (idx === 0) clone.dataset.bjPage = String(i + 1);
            frag.append(clone);
          });
          slots[i] = frag;
        } catch {
          slots[i] = null; // a failed page is skipped; the grid is slightly shorter
        }
      }
    };
    const pool = Math.min(REFETCH_CONCURRENCY, pageUrls.length);
    await Promise.all(Array.from({ length: pool }, worker));

    const grid = document.createDocumentFragment();
    for (const frag of slots) {
      if (frag) grid.append(frag); // appending a fragment moves its nodes, in order
    }
    if (grid.childElementCount) this._productList.replaceChildren(grid);
    if (capped) {
      console.warn(`[bj-infinite-scroll] restore capped at ${MAX_RESTORE_PAGES} pages`);
    }
  }

  // Toggle the product-list live region. It's `aria-live="polite"` so a single
  // appended page is announced, but a full-grid mutation (restore rebuild) or a
  // bulk append should be silenced first — otherwise a screen reader announces
  // every rebuilt card as new content (WCAG 4.1.3). Pass false around the write,
  // true after. All three grid-write sites funnel through here.
  _setGridAnnouncements(enabled) {
    this._productList?.setAttribute('aria-live', enabled ? 'polite' : 'off');
  }

  // Flip every lazily-loaded image in the restored grid to eager so they fetch
  // immediately. Only used on restore (reload/back-nav) — normal forward scroll
  // keeps native lazy-loading intact.
  _eagerizeImages() {
    if (!this._productList) return;
    this._productList.querySelectorAll('img[loading="lazy"]').forEach((img) => {
      img.loading = 'eager';
    });
  }

  // Cold deep-link entry: ?page=N opened with no saved snapshot (a shared or
  // indexed URL). The server renders page N alone, so re-fetch pages 1..N to
  // rebuild the full grid the visitor would have had by scrolling there, then
  // bring the collection section into view. Infinite scroll continues from the
  // server-set next URL (page N+1), or stops if N was the last page. Skipped on
  // the base collection URL (no page param) so a normal visit starts at the top.
  async _coldDeepLinkRestore() {
    if (!this._productList || !new URL(location.href).searchParams.has('page')) return;

    // Block the observer/button from appending while we rebuild (mirrors _restore).
    this._restoring = true;
    this._setGridAnnouncements(false);
    try {
      if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
      await this._rebuildByRefetch(location.href); // rebuilds pages 1..N (no-op on page 1)
      this._eagerizeImages();
      // The deep-linked URL is the furthest page loaded.
      this._deepestUrl = location.href;
    } finally {
      this._setGridAnnouncements(true);
      this._restoring = false;
    }
    // Let third-party card decorators re-init the rebuilt cards (and signal tests).
    document.dispatchEvent(new CustomEvent('bj:infinite-scroll:restored'));
    this._scrollToDeepLinkTarget();
  }

  // Scroll to the start of the deep-linked page: the card tagged data-bj-page="N"
  // (set when the page was appended / re-fetched), so the visitor lands where page
  // N begins with the earlier pages available by scrolling up. Falls back to the
  // collection section when there's no such marker (e.g. ?page=1).
  _scrollToDeepLinkTarget() {
    // Only a bare integer is a valid marker target; guard against a hand-crafted
    // ?page= value (e.g. containing a quote) throwing on an invalid selector.
    const pageParam = new URL(location.href).searchParams.get('page');
    const page = pageParam && /^\d+$/.test(pageParam) ? pageParam : null;
    const marker = page ? this._productList?.querySelector(`[data-bj-page="${page}"]`) : null;
    const section = this.dataset.sectionId
      ? document.getElementById(`shopify-section-${this.dataset.sectionId}`)
      : null;
    const target = marker || section || this._productList;
    if (!target) return;
    // Defer a frame so the re-fetched grid has laid out before we scroll.
    requestAnimationFrame(() => target.scrollIntoView({ block: 'start', behavior: 'auto' }));
  }

  // Reflect the page section currently at the top of the viewport in the URL. The
  // current page is the highest-numbered marker that has scrolled up into the top
  // zone (SPY_TRIGGER_RATIO of the viewport); page 1 has no marker, so it's the
  // default when nothing is above the line.
  _syncUrlToScrolledPage() {
    if (this._restoring || !this._productList) return;
    const markers = this._productList.querySelectorAll('[data-bj-page]');
    if (!markers.length) return;

    const triggerY = window.innerHeight * SPY_TRIGGER_RATIO;
    // At the very bottom the last (often short) page can't be scrolled up to the
    // trigger line, yet reaching the bottom means you're viewing it — so treat
    // hitting the bottom as being on the deepest loaded page.
    const atBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 2;

    let current = 1; // page 1 has no marker; the default when nothing qualifies
    markers.forEach((marker) => {
      const page = Number.parseInt(marker.dataset.bjPage, 10);
      if (!Number.isFinite(page) || page <= current) return;
      if (atBottom || marker.getBoundingClientRect().top <= triggerY + 1) current = page;
    });
    this._setUrlPage(current);
  }

  // Set (or clear, at page 1) the `page` param without touching filters/sort or
  // adding a history entry. No-ops when the URL already reflects `page`.
  _setUrlPage(page) {
    const url = new URL(location.href);
    const desired = page > 1 ? String(page) : null;
    if ((url.searchParams.get('page') || null) === desired) return;
    if (desired) url.searchParams.set('page', desired);
    else url.searchParams.delete('page');
    history.replaceState(null, '', url.pathname + url.search + url.hash);
    this._setCanonicalPage(desired);
  }

  // Keep <link rel="canonical"> in step with the page being viewed so each
  // paginated section self-references (page N canonicalises to .../?page=N)
  // rather than the whole scroll pointing at the entry URL. Only the `page`
  // param is touched — filters/sort stay off the canonical, matching Shopify's
  // server-rendered `canonical_url`.
  _setCanonicalPage(pageParam) {
    const link = document.querySelector('link[rel="canonical"]');
    if (!link) return;
    const canonical = new URL(link.href, location.origin);
    if (pageParam) canonical.searchParams.set('page', pageParam);
    else canonical.searchParams.delete('page');
    link.href = canonical.href;
  }

  _restoreScroll(y) {
    if (y <= 0) return;
    let frames = 0;
    const maxFrames = 90; // ~1.5s at 60fps
    const step = () => {
      window.scrollTo(0, y);
      const reachedTarget = Math.abs(window.scrollY - y) <= 2;
      const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
      const pageTooShort = y > maxScroll && Math.abs(window.scrollY - maxScroll) <= 2;
      if (!reachedTarget && !pageTooShort && frames++ < maxFrames) {
        requestAnimationFrame(step);
      }
    };
    requestAnimationFrame(step);
  }

  async _load() {
    if (this.hasAttribute('loading') || this._restoring || !this.dataset.nextUrl) return;
    const loadedUrl = this.dataset.nextUrl; // the page being fetched now (the new deepest)
    this.setAttribute('loading', '');

    try {
      const res = await fetch(loadedUrl, {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');

      const newItems = doc.querySelector('product-list')?.children ?? [];
      const insertIndex = this._productList.children.length;
      const loadedPage = new URL(loadedUrl, location.origin).searchParams.get('page');
      this._setGridAnnouncements(false);
      [...newItems].forEach((el, i) => {
        const clone = el.cloneNode(true);
        // Tag the first item of the appended page so any path can scroll to a
        // page's start via [data-bj-page="N"] (see _scrollToDeepLinkTarget).
        if (i === 0 && loadedPage) clone.dataset.bjPage = loadedPage;
        this._productList.append(clone);
      });
      this._setGridAnnouncements(true);
      // Reflect the current scroll position (the new marker may already be in the
      // top zone) — the address bar shows what's actually in view, not the just-
      // loaded page.
      this._syncUrlToScrolledPage();
      if (this._status && newItems.length) {
        this._status.textContent = `${doc.querySelectorAll('product-list product-card').length} more products loaded`;
      }

      this._deepestUrl = loadedUrl; // furthest page loaded (tracked for restore)
      document.title = doc.title;

      const nextEl = doc.querySelector('bj-jewellery-sets-scroll[data-next-url]');
      if (nextEl?.dataset.nextUrl) {
        this.dataset.nextUrl = nextEl.dataset.nextUrl;
        // Keep the SEO anchor in sync so Googlebot always sees the current next-page link
        const seoAnchor = document.querySelector('a[rel="next"]');
        if (seoAnchor) seoAnchor.href = nextEl.dataset.nextUrl;
        // Refresh the restoration snapshot with the newly appended grid.
        this._snapshot();
      } else {
        document.querySelector('a[rel="next"]')?.remove();
        this._observer?.disconnect();
        this._sentinel?.remove();
        // No more pages. Collapse to a hidden restore-only mount rather than
        // removing the element, so the scroll-spy observer stays alive and the URL
        // keeps tracking the scrolled page after the final page loads. Removing
        // data-next-url stops further loads and marks the terminal state (the
        // [data-next-url] selector no longer matches).
        delete this.dataset.nextUrl;
        this.classList.add('bj-infinite-scroll--restore-only');
        this._snapshot('');
        // Move focus to the first card of the final page ONLY in load_more mode,
        // where the user just activated a button that's now being removed. In
        // infinite_scroll mode the load is passive (IntersectionObserver during
        // scroll) with no preceding user action, so a focus jump would disorient
        // keyboard/AT users.
        const firstNew = this._productList.children[insertIndex];
        if (firstNew && this.dataset.mode === 'load_more') {
          const focusTarget = firstNew.querySelector('a, button') ?? firstNew;
          focusTarget.setAttribute('tabindex', '-1');
          focusTarget.focus({ preventScroll: true });
        }
      }
    } catch (err) {
      console.error('[bj-infinite-scroll]', err);
    } finally {
      this.removeAttribute('loading');
    }
  }

  disconnectedCallback() {
    this._observer?.disconnect();
    if (this._onScroll) window.removeEventListener('scroll', this._onScroll);
    if (this._spyRaf) cancelAnimationFrame(this._spyRaf);
    this._status?.remove();
    // Note: the pagehide handler is intentionally NOT removed here — it lives at
    // module scope so state is still saved after this element self-removes on the
    // final page (see _load / persistSnapshot).
  }
}

customElements.define('bj-jewellery-sets-scroll', BjJewellerySetsScroll);
